import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Search, Eye, Pencil, Users, Inbox, KanbanSquare, ReceiptText, FileText, ScrollText,
  MoreVertical, Plus, ArrowRight, Building2, CheckCircle2, Clock4, Wrench, X, Trash2, AlertTriangle, Archive, Smartphone, Send,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { canCreateClient } from "../../lib/clientPermissions";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, Avatar, Empty, Card, chf } from "@/components/common";

const STATUS_OPTS = [["aktiv", "Aktiv"], ["in_vorbereitung", "In Vorbereitung"], ["wartung", "Wartung"], ["pausiert", "Pausiert"], ["archiviert", "Archiviert"]];
const WEB_OPTS = [["online", "Online"], ["wartung", "Wartung"], ["offline", "Offline"], ["nicht_verbunden", "Nicht verbunden"]];
const POINT_OPTS = [["leads", "Anfragen offen"], ["tasks", "Aufgaben offen"], ["invoices", "Rechnung offen"]];
const APP_OPTS = [["yes", "Nutzt App"], ["no", "Nutzt App nicht"]];
const SORT_OPTS = [
  ["activity", "Letzte Aktivität"], ["name", "Kundenname"], ["status", "Status"],
  ["package", "Paket"], ["leads", "Offene Anfragen"], ["tasks", "Offene Aufgaben"],
];
const DOT = { rot: "bg-red-500", gelb: "bg-amber-400", gruen: "bg-[#A3E635]" };

const SummaryTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-[#121217] border border-white/10 rounded-xl px-4 py-3.5 flex items-center gap-3">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accent ? "bg-[#A3E635]/15" : "bg-white/5"}`}>
      <Icon className={`w-4 h-4 ${accent ? "text-[#A3E635]" : "text-gray-400"}`} />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-black tracking-tight text-white leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1 truncate">{label}</div>
    </div>
  </div>
);

function RowActions({ c, nav }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const go = (path) => { setOpen(false); nav(path); };
  const items = [
    { label: "Cockpit öffnen", icon: Eye, fn: () => go(`/admin/cockpit/${c.id}`), tid: `act-cockpit-${c.id}` },
    { label: "Kunde bearbeiten", icon: Pencil, fn: () => go(`/admin/clients/${c.id}`), tid: `act-edit-${c.id}` },
    { label: "Benutzer verwalten", icon: Users, fn: () => go(`/admin/clients/${c.id}?tab=users`), tid: `act-users-${c.id}` },
    { label: "Anfragen öffnen", icon: Inbox, fn: () => go(`/admin/clients/${c.id}?tab=leads`), tid: `act-leads-${c.id}` },
    { label: "Aufgaben öffnen", icon: KanbanSquare, fn: () => go(`/admin/clients/${c.id}?tab=tasks`), tid: `act-tasks-${c.id}` },
    { label: "Rechnungen öffnen", icon: ReceiptText, fn: () => go(`/admin/clients/${c.id}?tab=invoices`), tid: `act-invoices-${c.id}` },
    { label: "Monatsreport öffnen", icon: FileText, fn: () => go(`/admin/reports`), tid: `act-report-${c.id}` },
    { label: "Protokoll anzeigen", icon: ScrollText, fn: () => go(`/admin/clients/${c.id}?tab=protokoll`), tid: `act-log-${c.id}` },
  ];
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button data-testid={`actions-${c.id}`} title={t("Aktionen")} aria-label={`${t("Aktionen")}: ${c.company_name}`}
          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"><MoreVertical className="w-4 h-4" /></button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} collisionPadding={8} data-testid={`actions-menu-${c.id}`}
          className="z-50 w-52 max-w-[calc(100vw-16px)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-xl border border-white/10 bg-[#16161c] py-1.5 shadow-2xl outline-none">
          {items.map((it) => (
            <DropdownMenu.Item key={it.tid} onSelect={it.fn} data-testid={it.tid}
              className="w-full flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-gray-300 outline-none focus:bg-white/5 focus:text-white transition-colors text-left">
              <it.icon className="w-4 h-4 text-gray-500 shrink-0" /> {t(it.label)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export default function Clients() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fPackage, setFPackage] = useState("");
  const [fBranche, setFBranche] = useState("");
  const [fWeb, setFWeb] = useState("");
  const [fPoint, setFPoint] = useState("");
  const [fApp, setFApp] = useState("");
  const [sort, setSort] = useState("activity");
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const isSuper = user?.role === "super_admin";

  const reload = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { reload(); }, []);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids) => setSelected((s) => { const all = ids.every((i) => s.has(i)); return new Set(all ? [] : ids); });
  const runBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids: [...selected], confirm: bulkConfirm });
      toast.success(t("{n} Kunde(n) endgültig gelöscht").replace("{n}", data.deleted_count));
      setSelected(new Set()); setBulkModal(false); setBulkConfirm("");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("Sammel-Löschen fehlgeschlagen")); }
    finally { setBulkBusy(false); }
  };
  const runBulkArchive = async (archived) => {
    try {
      const { data } = await api.post("/clients/bulk-archive", { ids: [...selected], archived });
      toast.success((archived ? t("{n} Kunde(n) archiviert") : t("{n} Kunde(n) reaktiviert")).replace("{n}", data.count));
      setSelected(new Set());
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
  };
  const runAppInvite = async () => {
    setInviteBusy(true);
    try {
      const { data } = await api.post("/clients/app-invite", { ids: [...selected] });
      setInviteResult(data);
      const n = (data.prepared || 0) + (data.sent || 0);
      if (data.email_active) toast.success(t("{n} App-Einladung(en) gesendet").replace("{n}", data.sent));
      else toast.success(t("{n} App-Einladung(en) vorbereitet").replace("{n}", n));
      setSelected(new Set());
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setInviteBusy(false); }
  };

  const sel = "bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-[#A3E635]";

  const summary = useMemo(() => {
    const cs = clients || [];
    return {
      total: cs.length,
      aktiv: cs.filter((c) => c.status === "aktiv").length,
      vorbereitung: cs.filter((c) => c.status === "in_vorbereitung").length,
      wartung: cs.filter((c) => c.status === "wartung" || c.website_status === "wartung").length,
      leads: cs.reduce((s, c) => s + (c.open_leads || 0), 0),
      tasks: cs.reduce((s, c) => s + (c.open_tasks || 0), 0),
      invoices: cs.reduce((s, c) => s + (c.open_invoices_amount || 0), 0),
    };
  }, [clients]);

  const packages = useMemo(() => [...new Set((clients || []).map((c) => c.package?.name).filter(Boolean))], [clients]);
  const branchen = useMemo(() => [...new Set((clients || []).map((c) => c.branche).filter(Boolean))], [clients]);

  const view = useMemo(() => {
    let cs = [...(clients || [])];
    const ql = q.toLowerCase().trim();
    if (ql) cs = cs.filter((c) => [c.company_name, c.domain, c.branche, c.contact_person, c.email].filter(Boolean).some((v) => v.toLowerCase().includes(ql)));
    if (fStatus) cs = cs.filter((c) => c.status === fStatus);
    if (fPackage) cs = cs.filter((c) => c.package?.name === fPackage);
    if (fBranche) cs = cs.filter((c) => c.branche === fBranche);
    if (fWeb) cs = cs.filter((c) => (c.website_status || "nicht_verbunden") === fWeb);
    if (fPoint === "leads") cs = cs.filter((c) => (c.open_leads || 0) > 0);
    if (fPoint === "tasks") cs = cs.filter((c) => (c.open_tasks || 0) > 0);
    if (fPoint === "invoices") cs = cs.filter((c) => (c.open_invoices_amount || 0) > 0);
    if (fApp === "yes") cs = cs.filter((c) => c.has_app);
    if (fApp === "no") cs = cs.filter((c) => !c.has_app);
    cs = cs.filter((c) => c.client_type !== "test");  // Testsysteme nur im QA-Bereich
    if (!showArchived && fStatus !== "archiviert") cs = cs.filter((c) => c.status !== "archiviert");
    const cmp = {
      activity: (a, b) => (b.last_activity || "").localeCompare(a.last_activity || ""),
      name: (a, b) => a.company_name.localeCompare(b.company_name),
      status: (a, b) => (a.status || "").localeCompare(b.status || ""),
      package: (a, b) => (a.package?.name || "").localeCompare(b.package?.name || ""),
      leads: (a, b) => (b.open_leads || 0) - (a.open_leads || 0),
      tasks: (a, b) => (b.open_tasks || 0) - (a.open_tasks || 0),
    }[sort];
    return cs.sort(cmp);
  }, [clients, q, fStatus, fPackage, fBranche, fWeb, fPoint, fApp, sort, showArchived]);

  const archivedCount = useMemo(() => (clients || []).filter((c) => c.status === "archiviert").length, [clients]);

  const resetFilters = () => { setQ(""); setFStatus(""); setFPackage(""); setFBranche(""); setFWeb(""); setFPoint(""); setFApp(""); };
  const activeFilters = q || fStatus || fPackage || fBranche || fWeb || fPoint || fApp;

  if (!clients) return <Spinner />;

  return (
    <div className="fade-in w-full min-w-0">
      <PageHeader title={t("clients")} subtitle={t("{n} Mandanten · {a} aktiv").replace("{n}", clients.length).replace("{a}", summary.aktiv)}>
        {canCreateClient(user) && <Link to="/admin/clients/new" data-testid="clients-new-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("newClient")}
        </Link>}
      </PageHeader>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6" data-testid="clients-summary">
        <SummaryTile testid="sum-total" icon={Building2} label={t("Kunden gesamt")} value={summary.total} accent />
        <SummaryTile testid="sum-aktiv" icon={CheckCircle2} label={t("Aktiv")} value={summary.aktiv} />
        <SummaryTile testid="sum-vorbereitung" icon={Clock4} label={t("In Vorbereitung")} value={summary.vorbereitung} />
        <SummaryTile testid="sum-wartung" icon={Wrench} label={t("Wartung")} value={summary.wartung} />
        <SummaryTile testid="sum-leads" icon={Inbox} label={t("Offene Anfragen")} value={summary.leads} />
        <SummaryTile testid="sum-tasks" icon={KanbanSquare} label={t("Offene Aufgaben")} value={summary.tasks} />
        <SummaryTile testid="sum-invoices" icon={ReceiptText} label={t("Offene Rechnungen")} value={chf(summary.invoices)} />
      </div>

      {/* SEARCH + FILTERS */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Firma, Domain, Branche, Kontakt, E-Mail…")}
            className="w-full bg-black border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#A3E635]" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel}>
            <option value="">{t("Status: Alle")}</option>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </select>
          <select data-testid="filter-package" value={fPackage} onChange={(e) => setFPackage(e.target.value)} className={sel}>
            <option value="">{t("Paket: Alle")}</option>{packages.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="filter-branche" value={fBranche} onChange={(e) => setFBranche(e.target.value)} className={sel}>
            <option value="">{t("Branche: Alle")}</option>{branchen.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="filter-website" value={fWeb} onChange={(e) => setFWeb(e.target.value)} className={sel}>
            <option value="">{t("Website: Alle")}</option>{WEB_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </select>
          <select data-testid="filter-points" value={fPoint} onChange={(e) => setFPoint(e.target.value)} className={sel}>
            <option value="">{t("Offene Punkte: Alle")}</option>{POINT_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </select>
          <select data-testid="filter-app" value={fApp} onChange={(e) => setFApp(e.target.value)} className={sel}>
            <option value="">{t("App: Alle")}</option>{APP_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </select>
          <select data-testid="sort-by" value={sort} onChange={(e) => setSort(e.target.value)} className={sel}>
            {SORT_OPTS.map(([v, l]) => <option key={v} value={v}>{t("Sortieren:")} {t(l)}</option>)}
          </select>
          {archivedCount > 0 && (
            <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"
              className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors ${showArchived ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}>
              <Archive className="w-3.5 h-3.5" /> {showArchived ? t("Archivierte ausblenden") : t("Archivierte anzeigen ({n})").replace("{n}", archivedCount)}
            </button>
          )}
          {activeFilters && (
            <button onClick={resetFilters} data-testid="filter-reset" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 px-2 py-2">
              <X className="w-3.5 h-3.5" /> {t("Zurücksetzen")}
            </button>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2">{t("{n} von {total} Kunden").replace("{n}", view.length).replace("{total}", clients.length)}</div>

      {/* DESKTOP TABLE */}
      {/* Bulk-Aktionsleiste (nur Super-Admin) */}
      {isSuper && selected.size > 0 && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 mb-3 p-3 rounded-xl bg-red-500/[0.07] border border-red-500/30" data-testid="clients-bulk-bar">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-gray-200" data-testid="bulk-selected-count">{t("{n} Kunde(n) ausgewählt").replace("{n}", selected.size)}</span>
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear" className="min-h-10 text-xs text-gray-400 hover:text-white px-3 py-1.5">{t("Auswahl aufheben")}</button>
          </div>
          <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
            <button onClick={() => { setInviteResult(null); setInviteModal(true); }} data-testid="bulk-app-invite-btn"
              className="col-span-2 sm:col-span-1 min-w-0 min-h-11 bg-[#A3E635]/15 hover:bg-[#A3E635]/25 border border-[#A3E635]/40 text-[#A3E635] font-semibold py-2 px-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"><Send className="w-4 h-4 shrink-0" /><span className="min-w-0 break-words">{t("App-Einladung senden")}</span></button>
            <button onClick={() => runBulkArchive(true)} data-testid="bulk-archive-btn"
              className="min-w-0 min-h-11 bg-white/5 hover:bg-white/10 border border-white/15 text-gray-200 font-semibold py-2 px-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"><Archive className="w-4 h-4 shrink-0" /><span className="min-w-0 break-words">{t("Archivieren")}</span></button>
            <button onClick={() => { setBulkConfirm(""); setBulkModal(true); }} data-testid="bulk-delete-btn"
              className="min-w-0 min-h-11 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"><Trash2 className="w-4 h-4 shrink-0" /><span className="min-w-0 break-words">{t("Ausgewählte löschen")}</span></button>
          </div>
        </div>
      )}

      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="bulk-delete-modal">
          <div className="bg-[#121217] border border-red-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
              <h3 className="text-lg font-bold text-white">{t("{n} Kunde(n) endgültig löschen").replace("{n}", selected.size)}</h3>
            </div>
            <p className="text-sm text-gray-400">{t("Folgende Kunden werden inkl. ALLER Daten (Website, Benutzer, Anfragen, Aufgaben, Rechnungen, Reports, Module, Support) unwiderruflich gelöscht:")}</p>
            <ul className="mt-2 max-h-32 overflow-auto text-sm text-white list-disc list-inside space-y-0.5" data-testid="bulk-delete-list">
              {(clients || []).filter((c) => selected.has(c.id)).map((c) => <li key={c.id}>{c.company_name}</li>)}
            </ul>
            <p className="text-xs text-gray-500 mt-3">{t("Zur Bestätigung")} <span className="font-bold text-red-300">LÖSCHEN</span> {t("eingeben:")}</p>
            <input autoFocus value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder="LÖSCHEN" data-testid="bulk-confirm-input"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-400" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setBulkModal(false)} data-testid="bulk-cancel" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 rounded-lg text-sm">{t("Abbrechen")}</button>
              <button onClick={runBulkDelete} disabled={bulkBusy || bulkConfirm.trim().toUpperCase() !== "LÖSCHEN"} data-testid="bulk-confirm-btn"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed">{bulkBusy ? t("Lösche …") : t("Endgültig löschen")}</button>
            </div>
          </div>
        </div>
      )}

      {inviteModal && (() => {
        const selList = (clients || []).filter((c) => selected.has(c.id));
        const withApp = selList.filter((c) => c.has_app);
        const noApp = selList.filter((c) => !c.has_app);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="app-invite-modal">
            <div className="bg-[#121217] border border-[#A3E635]/30 rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#A3E635]/15 flex items-center justify-center shrink-0"><Smartphone className="w-5 h-5 text-[#A3E635]" /></div>
                <h3 className="text-lg font-bold text-white">{t("App-Einladung senden")}</h3>
              </div>

              {!inviteResult ? (
                <>
                  <p className="text-sm text-gray-400">{t("Die ausgewählten Kunden erhalten eine Einladung mit der Anleitung, wie sie das Cockpit als App auf dem Smartphone installieren.")}</p>
                  <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 p-3 max-h-40 overflow-auto" data-testid="app-invite-list">
                    <div className="text-[11px] uppercase tracking-wider text-[#A3E635] font-bold mb-1">{t("Werden eingeladen")} ({noApp.length})</div>
                    {noApp.length === 0 ? <p className="text-xs text-gray-500">{t("Keine — alle ausgewählten Kunden nutzen die App bereits.")}</p> :
                      <ul className="text-sm text-white list-disc list-inside space-y-0.5">{noApp.map((c) => <li key={c.id}>{c.company_name}</li>)}</ul>}
                    {withApp.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-white/5">
                        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1">{t("Übersprungen (nutzt App bereits)")} ({withApp.length})</div>
                        <ul className="text-xs text-gray-500 list-disc list-inside space-y-0.5">{withApp.map((c) => <li key={c.id}>{c.company_name}</li>)}</ul>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-amber-300/90 mt-3">{t("Hinweis: Der tatsächliche E-Mail-Versand erfolgt, sobald die E-Mail-Domain (Brevo/DNS) freigeschaltet ist. Bis dahin werden die Einladungen ehrlich «vorbereitet».")}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setInviteModal(false)} data-testid="app-invite-cancel" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 rounded-lg text-sm">{t("Abbrechen")}</button>
                    <button onClick={runAppInvite} disabled={inviteBusy || noApp.length === 0} data-testid="app-invite-confirm"
                      className="flex-1 bg-[#A3E635] hover:brightness-105 text-black font-bold py-2 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" /> {inviteBusy ? t("Wird vorbereitet …") : t("{n} einladen").replace("{n}", noApp.length)}</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-white/[0.03] border border-white/10 p-4 space-y-1.5" data-testid="app-invite-result">
                    <div className="flex items-center justify-between text-sm"><span className="text-gray-400">{inviteResult.email_active ? t("Gesendet") : t("Vorbereitet")}</span><span className="font-bold text-[#A3E635]">{(inviteResult.prepared || 0) + (inviteResult.sent || 0)}</span></div>
                    {inviteResult.skipped_has_app > 0 && <div className="flex items-center justify-between text-sm"><span className="text-gray-400">{t("Übersprungen (nutzt App)")}</span><span className="font-semibold text-gray-300">{inviteResult.skipped_has_app}</span></div>}
                    {inviteResult.skipped_no_email > 0 && <div className="flex items-center justify-between text-sm"><span className="text-gray-400">{t("Übersprungen (keine E-Mail)")}</span><span className="font-semibold text-gray-300">{inviteResult.skipped_no_email}</span></div>}
                  </div>
                  {!inviteResult.email_active && <p className="text-xs text-amber-300/90 mt-3">{t("Die Einladungen sind vorbereitet. Sie werden automatisch versendet, sobald der E-Mail-Versand (Brevo/DNS) freigeschaltet ist.")}</p>}
                  <button onClick={() => setInviteModal(false)} data-testid="app-invite-close" className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 rounded-lg text-sm">{t("Schliessen")}</button>
                </>
              )}
            </div>
          </div>
        );
      })()}


      <Card className="hidden xl:block min-w-0" data-testid="clients-table-region">
        <table className="w-full table-fixed text-left" data-testid="clients-table">
          <colgroup>
            {isSuper && <col className="w-9" />}
            <col />
            <col className="hidden min-[1920px]:table-column w-28" />
            <col className="w-24" />
            <col className="hidden min-[1920px]:table-column w-28" />
            <col className="w-12" /><col className="w-12" />
            <col className="hidden min-[1920px]:table-column w-28" />
            <col className="hidden min-[1920px]:table-column w-24" />
            <col className="w-28" /><col className="w-36" /><col className="w-28" />
          </colgroup>
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
              {isSuper && <th className="px-2 py-3 w-10"><input type="checkbox" data-testid="select-all" checked={view.length > 0 && view.every((c) => selected.has(c.id))} onChange={() => toggleAll(view.map((c) => c.id))} className="accent-[#A3E635] w-4 h-4 cursor-pointer" /></th>}
              <th className="px-2 py-3 font-bold">{t("Kunde")}</th>
              <th className="px-2 py-3 font-bold hidden min-[1920px]:table-cell">{t("Branche")}</th>
              <th className="px-2 py-3 font-bold">{t("Paket")}</th>
              <th className="px-2 py-3 font-bold hidden min-[1920px]:table-cell">{t("Website")}</th>
              <th className="px-2 py-3 font-bold text-center">{t("Anfr.")}</th>
              <th className="px-2 py-3 font-bold text-center">{t("Aufg.")}</th>
              <th className="px-2 py-3 font-bold text-right hidden min-[1920px]:table-cell">{t("Rechnung")}</th>
              <th className="px-2 py-3 font-bold hidden min-[1920px]:table-cell">{t("Letzte Akt.")}</th>
              <th className="px-2 py-3 font-bold">{t("Status")}</th>
              <th className="px-2 py-3 font-bold">{t("Nächste Aktion")}</th>
              <th className="px-2 py-3 font-bold text-right">{t("Aktionen")}</th>
            </tr>
          </thead>
          <tbody>
            {view.map((c) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`client-tr-${c.id}`}>
                {isSuper && <td className="px-2 py-3"><input type="checkbox" data-testid={`select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} className="accent-[#A3E635] w-4 h-4 cursor-pointer" /></td>}
                <td className="px-2 py-3">
                  <Link to={`/admin/clients/${c.id}`} data-testid={`client-row-${c.id}`} className="flex min-w-0 items-center gap-2 group">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${DOT[c.attention] || DOT.gruen}`} />
                    <Avatar name={c.company_name} color={c.primary_color} src={c.logo_url || null} size={34} />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm group-hover:text-[#A3E635] transition-colors flex flex-wrap items-center gap-1">
                        <span className="min-w-0 break-words [overflow-wrap:anywhere]">{c.company_name}</span>
                        {c.client_type === "internal" && <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[#A3E635]/15 text-[#A3E635] text-[10px] font-bold uppercase tracking-wider" data-testid={`internal-badge-${c.id}`}>{t("Intern")}</span>}
                        {c.has_app && <Smartphone className="shrink-0 w-3.5 h-3.5 text-[#A3E635]" data-testid={`app-badge-${c.id}`} title={`${t("Nutzt App")}${c.app_devices ? ` · ${c.app_devices} ${c.app_devices === 1 ? "Gerät" : "Geräte"}` : ""}`} />}
                      </div>
                      <div className="text-xs text-gray-500 truncate" title={c.domain}>{c.domain}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-2 py-3 text-sm text-gray-400 hidden min-[1920px]:table-cell break-words [overflow-wrap:anywhere]">{c.branche}</td>
                <td className="px-2 py-3"><span className="px-2 py-0.5 rounded-md bg-white/5 text-xs break-words [overflow-wrap:anywhere]">{c.client_type === "internal" ? t("Intern / KEWAG") : (c.package?.name || "—")}</span></td>
                <td className="px-2 py-3 hidden min-[1920px]:table-cell"><StatusBadge status={c.website_status} /></td>
                <td className="px-2 py-3 text-center"><span className={`text-sm font-bold ${(c.open_leads || 0) > 0 ? "text-amber-300" : "text-gray-600"}`}>{c.open_leads || 0}</span></td>
                <td className="px-2 py-3 text-center"><span className={`text-sm font-bold ${(c.overdue_tasks || 0) > 0 ? "text-red-300" : (c.open_tasks || 0) > 0 ? "text-white" : "text-gray-600"}`}>{c.open_tasks || 0}</span></td>
                <td className="px-2 py-3 text-right hidden min-[1920px]:table-cell"><span className={`text-sm break-words ${(c.open_invoices_amount || 0) > 0 ? "text-white font-semibold" : "text-gray-600"}`}>{chf(c.open_invoices_amount)}</span></td>
                <td className="px-2 py-3 hidden min-[1920px]:table-cell text-xs text-gray-400">{c.last_activity ? c.last_activity.slice(0, 10) : "—"}</td>
                <td className="px-2 py-3 [overflow-wrap:anywhere]"><StatusBadge status={c.status} /></td>
                <td className="px-2 py-3"><span className="text-xs text-gray-300 inline-flex items-start gap-1"><ArrowRight className="w-3 h-3 shrink-0 mt-0.5 text-[#A3E635]" /><span className="min-w-0 break-words [overflow-wrap:anywhere]">{c.next_action}</span></span></td>
                <td className="px-2 py-3">
                  <div className="flex items-center justify-end gap-0.5">
                    <button onClick={() => nav(`/admin/cockpit/${c.id}`)} data-testid={`view-as-${c.id}`} title={t("Cockpit öffnen")}
                      className="p-2 rounded-lg text-gray-400 hover:text-[#A3E635] hover:bg-white/5 transition-colors"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`edit-${c.id}`} title={t("Kunde bearbeiten")}
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"><Pencil className="w-4 h-4" /></button>
                    <RowActions c={c} nav={nav} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {view.length === 0 && <Empty text={t("noData")} />}
      </Card>

      {/* MOBILE CARDS */}
      <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="clients-mobile">
        {view.length === 0 ? <Empty text={t("noData")} /> : view.map((c) => (
          <Card key={c.id} className="p-4 min-w-0" data-testid={`client-card-${c.id}`}>
            <div className="flex items-center gap-3">
              {isSuper && <input type="checkbox" aria-label={`${t("Kunde auswählen")}: ${c.company_name}`} data-testid={`card-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} className="accent-[#A3E635] w-4 h-4 shrink-0 cursor-pointer" />}
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT[c.attention] || DOT.gruen}`} />
              <Avatar name={c.company_name} color={c.primary_color} src={c.logo_url || null} size={40} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm break-words [overflow-wrap:anywhere]">{c.company_name}{c.has_app && <Smartphone className="inline-block ml-1 w-3.5 h-3.5 text-[#A3E635]" />}</div>
                <div className="text-xs text-gray-500 truncate">{c.domain}</div>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs">
              <span className="px-2 py-0.5 rounded-md bg-white/5">{c.package?.name || "—"}</span>
              <span className="text-gray-400"><span className="text-white font-semibold">{c.open_leads || 0}</span> {t("Anfragen")}</span>
              <span className="text-gray-400"><span className="text-white font-semibold">{c.open_tasks || 0}</span> {t("Aufgaben")}</span>
            </div>
            <div className="text-xs text-[#A3E635] mt-2 flex items-start gap-1"><ArrowRight className="w-3 h-3 shrink-0 mt-0.5" /><span className="min-w-0 break-words [overflow-wrap:anywhere]">{c.next_action}</span></div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`card-open-${c.id}`}
                className="flex-1 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 rounded-lg text-sm">{t("Öffnen")}</button>
              <button onClick={() => nav(`/admin/cockpit/${c.id}`)} className="px-4 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white text-sm inline-flex items-center gap-1">
                <Eye className="w-4 h-4" /> {t("Cockpit")}
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
